import React from 'react';

import { ResourceHeatmap as ResourceHeatmapType } from '../../models/CommandCenter';

interface ResourceHeatmapProps {
  heatmap: ResourceHeatmapType[];
}

export const ResourceHeatmapComponent: React.FC<ResourceHeatmapProps> = ({ heatmap }) => {
  const getUtilizationColor = (utilization: number) => {
    if (utilization >= 90) return 'bg-red-500';
    if (utilization >= 80) return 'bg-orange-500';
    if (utilization >= 70) return 'bg-yellow-500';
    if (utilization >= 50) return 'bg-[#4267A1]';
    return 'bg-[#8A9150]/30';
  };

  const getUtilizationTextColor = (utilization: number) => {
    if (utilization >= 90) return 'text-red-700';
    if (utilization >= 80) return 'text-orange-700';
    if (utilization >= 70) return 'text-yellow-700';
    if (utilization >= 50) return 'text-[#4267A1]';
    return 'text-[#8A9150]';
  };

  const formatHours = (hours: number) => {
    return `${hours.toFixed(1)}h`;
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage.toFixed(1)}%`;
  };

  return (
    <div className="space-y-6">
      {/* Legend */}
      <div className="flex items-center space-x-4 text-sm">
        <span className="text-gray-800 font-medium">Utilization:</span>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-[#8A9150]/30 rounded"></div>
          <span className="text-gray-700">&lt;50%</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-[#4267A1] rounded"></div>
          <span className="text-gray-700">50-70%</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-yellow-500 rounded"></div>
          <span className="text-gray-700">70-80%</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-orange-500 rounded"></div>
          <span className="text-gray-700">80-90%</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-red-500 rounded"></div>
          <span className="text-gray-700">&gt;90%</span>
        </div>
      </div>

      {/* Heatmap Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {heatmap.map((resource, index) => (
          <div key={index} className="geex-card p-6 rounded-xl bg-white border border-gray-200 shadow-lg hover:shadow-xl transition-all min-h-[280px]">
            <div className="mb-6">
              <div className="mb-4">
                <h4 className="text-xl font-bold text-gray-900 truncate mb-2">{resource.role}</h4>
                <p className="text-base text-gray-600">{resource.team_members} team members</p>
              </div>
              <div className="flex justify-end">
                <div className={`px-4 py-2 rounded-full text-sm font-semibold ${getUtilizationTextColor(resource.utilization)} ${getUtilizationColor(resource.utilization).replace('bg-', 'bg-opacity-20')}`}>
                  {formatPercentage(resource.utilization)}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between text-base">
                <span className="text-gray-600">Billable Hours:</span>
                <span className="font-semibold text-gray-900">{formatHours(resource.billable_hours)}</span>
              </div>
              <div className="flex justify-between text-base">
                <span className="text-gray-600">Total Hours:</span>
                <span className="font-semibold text-gray-900">{formatHours(resource.total_hours)}</span>
              </div>
              <div className="flex justify-between text-base">
                <span className="text-gray-600">Capacity:</span>
                <span className="font-semibold text-gray-900">{formatHours(resource.capacity)}</span>
              </div>
            </div>

            {/* Utilization Bar */}
            <div className="mt-6">
              <div className="flex justify-between text-sm text-gray-600 mb-3">
                <span>Utilization</span>
                <span className="font-medium">{formatPercentage(resource.utilization)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full ${getUtilizationColor(resource.utilization)}`}
                  style={{ width: `${Math.min(resource.utilization, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Billable Percentage */}
            <div className="mt-5">
              <div className="flex justify-between text-sm text-gray-600 mb-3">
                <span>Billable Efficiency</span>
                <span className="font-medium">{formatPercentage((resource.billable_hours / resource.total_hours) * 100)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="h-3 rounded-full bg-[#8A9150]"
                  style={{ width: `${(resource.billable_hours / resource.total_hours) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary Stats */}
      {heatmap.length > 0 && (
        <div className="bg-white border border-gray-200 shadow-lg rounded-xl p-6">
          <h4 className="text-xl font-semibold text-gray-900 mb-6">Portfolio Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-base">
            <div className="text-center">
              <p className="text-gray-600 mb-2">Total Team Members</p>
              <p className="text-2xl font-bold text-gray-900">
                {heatmap.reduce((sum, r) => sum + r.team_members, 0)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-gray-600 mb-2">Avg Utilization</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatPercentage(heatmap.reduce((sum, r) => sum + r.utilization, 0) / heatmap.length)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-gray-600 mb-2">Total Billable Hours</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatHours(heatmap.reduce((sum, r) => sum + r.billable_hours, 0))}
              </p>
            </div>
            <div className="text-center">
              <p className="text-gray-600 mb-2">Avg Billable Efficiency</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatPercentage(
                  (heatmap.reduce((sum, r) => sum + r.billable_hours, 0) / 
                   heatmap.reduce((sum, r) => sum + r.total_hours, 0)) * 100
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
